import Vue from 'vue';
import gql from 'graphql-tag';

const emptyAddress = {
    id: null,
    firstName: null,
    lastName: null,
    street: null,
    postCode: null,
    city: null,
    salutation: null,
    country: '',
    packingStation: null,
    packingStationPostCode: null,
    fiscalCode: null,
    houseNumber: null,
    birthday: null,
    phone: null,
};

export default {
    namespaced: true,
    state: {
        loading: false,
        address: Object.assign({}, emptyAddress),
    },
    getters: {
        getFirstName: state => state.address.firstName,
        getLastName: state => state.address.lastName,
        getStreet: state => state.address.street,
        getPostCode: state => state.address.postCode,
        getCity: state => state.address.city,
        getSalutation: state => state.address.salutation,
        getCountry: state => state.address.country,
        getPackingStation: state => state.address.packingStation,
        getPackingStationPostCode: state => state.address.packingStationPostCode,
        getFiscalCode: state => state.address.fiscalCode,
        getHouseNumber: state => state.address.houseNumber,
        getBirthday: state => state.address.birthday,
        getPhone: state => state.address.phone,
        getUid: state => state.address.uid,
        getCompany: state => state.address.company,
    },
    mutations: {
        setFirstName(state, value) {
            Vue.set(state.address, 'firstName', value);
        },
        setLastName(state, value) {
            Vue.set(state.address, 'lastName', value);
        },
        setStreet(state, value) {
            Vue.set(state.address, 'street', value);
        },
        setPostCode(state, value) {
            Vue.set(state.address, 'postCode', value);
        },
        setCity(state, value) {
            Vue.set(state.address, 'city', value);
        },
        setSalutation(state, value) {
            Vue.set(state.address, 'salutation', value);
        },
        setCountry(state, value) {
            Object.values(Vue.$country.getCountries()).some(country => {
                if (value === country.iso_2) {
                    Vue.set(state.address, 'country', country.iso_2);
                    return true;
                } else if (value === country.name) {
                    Vue.set(state.address, 'country', country.iso_2);
                    return true;
                }
            });
        },
        setPackingStation(state, value) {
            Vue.set(state.address, 'packingStation', value);
        },
        setPackingStationPostCode(state, value) {
            Vue.set(state.address, 'packingStationPostCode', value);
        },
        setFiscalCode(state, value) {
            Vue.set(state.address, 'fiscalCode', value);
        },
        setHouseNumber(state, value) {
            Vue.set(state.address, 'houseNumber', value);
        },
        setBirthday(state, value) {
            Vue.set(state.address, 'birthday', value);
        },
        setPhone(state, value) {
            Vue.set(state.address, 'phone', value);
        },
        setUid(state, value) {
            Vue.set(state.address, 'uid', value);
        },
        setCompany(state, value) {
            Vue.set(state.address, 'company', value);
        },
        setAddress(state, values) {
            if (values) {
                Object.keys(values).forEach(indexName => {
                    if (state.address.hasOwnProperty(indexName)) {
                        if (indexName === 'country') {
                            Object.values(Vue.$country.getCountries()).some(country => {
                                if (values[indexName] === country.iso_2) {
                                    Vue.set(state.address, 'country', country.iso_2);
                                    return true;
                                }
                            });
                        } else {
                            Vue.set(state.address, indexName, values[indexName]);
                        }
                    }
                });
            }
        },
        setLoading(state, value) {
            Vue.set(state, 'loading', value);
        },
        resetAddress(state, value) {
            for (let field of Object.keys(emptyAddress)) {
                Vue.set(state.address, field, emptyAddress[field]);
            }
        },
    },
    actions: {
        loadAddress({ commit, rootGetters }) {
            if (rootGetters['Login/isUserLoggedIn']) {
                return Vue.$graphql
                    .query({
                        query: gql`
                            query getBillingAddresses {
                                getBillingAddresses {
                                    id
                                    city
                                    country
                                    firstName
                                    lastName
                                    postCode
                                    salutation
                                    street
                                    packingStation
                                    packingStationPostCode
                                    fiscalCode
                                    houseNumber
                                    birthday
                                    phone
                                    company
                                    uid
                                }
                            }
                        `,
                        fetchPolicy: 'no-cache',
                    })
                    .then(r => r.data)
                    .then(data => {
                        let address;
                        if (data.getBillingAddresses.length > 0) {
                            address = data.getBillingAddresses[data.getBillingAddresses.length - 1];
                        }
                        commit('setAddress', address);

                        return address;
                    })
                    .catch(() => {
                        return {};
                    });
            } else {
                return new Promise(resolve => {
                    resolve();
                });
            }
        },
        saveAddress({ state, commit }, addressData) {
            //@todo: We need to detect if there were any changes to address done and to not save it if it's the same
            commit('setLoading', true);

            const headers = {};

            if (!addressData) {
                addressData = {
                    ...state.address,
                    birthday: state.address.birthday ? (new Date(state.address.birthday)).getTime() : undefined,
                };
            } else if (addressData.token) {
                headers.authorization = `Bearer ${addressData.token}`;
                delete addressData.token;
            }

            this.dispatch('Customer/validatePriceGroup', { addressData, headers }, { root: true });

            return Vue.$graphql
                .mutate({
                    mutation: gql`
                        mutation($addressData: CustomerBillingAddressInput!) {
                            addBillingAddress(addressData: $addressData) {
                                id
                                city
                                country
                                firstName
                                lastName
                                postCode
                                salutation
                                street
                                packingStation
                                packingStationPostCode
                                fiscalCode
                                houseNumber
                                birthday
                                phone
                                company
                                uid
                            }
                        }
                    `,
                    variables: {
                        addressData,
                    },
                    context: {
                        headers,
                    },
                })
                .then(r => r.data)
                .then(result => {
                    let address = result.addBillingAddress;
                    commit('setAddress', address);
                    commit('setLoading', false);

                    return address;
                })
                .catch(() => {
                    commit('setLoading', false);
                    return {};
                });
        },
    },
};
